import { useCallback, useEffect, useMemo, useState } from "react";
import { BtnExportarDadosProps, TOpenModal } from ".";
import { useQuery } from "react-query";
import axiosClient from "../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import LinearProgressWithLabel from "../LinearProgressWithLabel";
import extrairNomesIdArray, { generatePDFTable } from "../../helpers/extrairNomeIdObj";
import extrairChavesUnicas from "../../helpers/extrairChaveUnicaArray";
import moment from "moment";

export default function DialogProcessoExportar({
    fileName,
    pathRequisicao,
    open,
    handleClose,
    queryParams,
    formatos,
    exportType,
    cabecalhoLabels = [],
    possuiQuery = false,
    dadosArray = [],
    exportTitle,
    exportInfo,
}: DialogProcessoExportarProps) {

    const limiteQuery = 100

    const [csvData, setCsvData] = useState<Array<any>>([])
    const [pagina, setPagina] = useState(0)
    const [totalData, setTotalData] = useState(0)
    const [primeiraRequisicaoConcluida, setPrimeiraRequisicaoConcluida] = useState(false)
    const [perPage, setPerPage] = useState(100)

    const formatosCombinados = useMemo(() => {
        const fromLabels: { [key: string]: string } = {}
        cabecalhoLabels.forEach(i => { if (i.tipo) fromLabels[i.label] = i.tipo })
        return { ...fromLabels, ...(formatos || {}) } as { [key: string]: string }
    }, [cabecalhoLabels, formatos])

    useQuery({
        queryKey: [`export-csv-${pathRequisicao}`, pagina],
        queryFn: async () => {
            const response = await axiosClient.get(`/${pathRequisicao}`, {
                params: {
                    ...queryParams,
                    page: pagina + 1,
                    limit: limiteQuery,
                },
            })
            const data = response?.data?.data.map(transformarChaveUnica) || [];
            const total = response?.data.total || 0;
            const perPageFromApi = response?.data.per_page || 100;

            setTotalData(total)
            setCsvData(prevState => [...prevState, ...data])
            setPrimeiraRequisicaoConcluida(true)
            setPerPage(perPageFromApi)
            
            const dadosBuscadosAtePaginaAtual = perPageFromApi * (pagina + 1);
            if (dadosBuscadosAtePaginaAtual < total) {
                setTimeout(() => {
                    setPagina(prevState => prevState + 1);
                }, 500);
            }
            
            return response
        },
        cacheTime: 0,
        enabled: possuiQuery
    });

    const transformarChaveUnica = useCallback((obj: any) => {
        let newObj: any = {};
        const orderedKeys = cabecalhoLabels.map((item) => item.label);
    
        orderedKeys.forEach((key) => {
            const mapeamento = cabecalhoLabels.find((item) => item.label === key);
            if (mapeamento) {
                newObj[key] = obj[mapeamento.queryKey];
            }
        });
    
        return newObj;
    }, [cabecalhoLabels]);
    const valorProgresso = useMemo((): number => {
        if (!possuiQuery) return 100
        if (!primeiraRequisicaoConcluida) return 0 
        if (totalData === 0) return 100
        if (csvData.length === 0) return 0

        const dadosBuscadosAtePaginaAtual = perPage * (pagina + 1);
        const porcentagemFaltante = (dadosBuscadosAtePaginaAtual / totalData) * 100;
        if (porcentagemFaltante > 100) return 100

        return parseFloat(porcentagemFaltante.toFixed(2));
    }, [pagina, totalData, csvData, possuiQuery, primeiraRequisicaoConcluida, perPage])

    useEffect(() => {
        if (!possuiQuery && dadosArray.length > 0) {
            const data = dadosArray.map(transformarChaveUnica) || [];
            setCsvData(data)
        }
    }, [possuiQuery, dadosArray, transformarChaveUnica])
    
    useEffect(() => {
        if (valorProgresso === 100 && primeiraRequisicaoConcluida) {
            if (csvData.length > 0) {
                if (exportType === "pdf") {
                    const headers = extrairChavesUnicas(csvData) as string[]
                    const rightAlignHeaders = headers.filter(h => formatosCombinados[h] === "monetario")
                    generatePDFTable(
                        extrairNomesIdArray(csvData, formatosCombinados),
                        headers,
                        `${fileName}_${moment().format("DDMMYYYY_HH_mm")}`,
                        {
                            title: exportTitle,
                            info: exportInfo,
                            rightAlignHeaders
                        }
                    )
                }
                
                enqueueSnackbar("Exportação realizada com sucesso!", { variant: "success" })
            } else {
                enqueueSnackbar("Nenhum dado encontrado para exportar.", { variant: "info" })
            }
            handleClose()
        }
    }, [valorProgresso, primeiraRequisicaoConcluida, csvData, exportType, fileName, formatosCombinados, exportTitle, exportInfo, handleClose])

    return (
        <>
            <Dialog
                open={open}
                fullWidth
            >
                <DialogTitle >
                    Exportando arquivo .{exportType}
                </DialogTitle>

                <DialogContent>
                    <LinearProgressWithLabel value={valorProgresso} />
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleClose}
                    >
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>

            {
                (valorProgresso === 100 && csvData.length > 0 && exportType === "csv") &&
                (() => {
                    const headers = extrairChavesUnicas(csvData) as string[];
                    const body = extrairNomesIdArray(csvData, formatosCombinados);
                    const lines: string[] = [];
                    if (exportTitle) lines.push(["Relatório", exportTitle].join(";"));
                    const now = moment().format("DD/MM/YYYY HH:mm");
                    const hasDataGeracao = (exportInfo || []).some(i => i.label.trim().toLowerCase() === "data de geração".toLowerCase());
                    if (!hasDataGeracao) lines.push(["Data de geração", now].join(";"));
                    if (exportInfo && exportInfo.length > 0) {
                        exportInfo.forEach(i => lines.push([i.label, i.value].join(";")));
                    }
                    lines.push("");
                    lines.push(headers.join(";"));
                    body.forEach((row: any) => {
                        const vals = headers.map(h => String(row[h] ?? ""));
                        lines.push(vals.join(";"));
                    });
                    const csvContent = "\uFEFF" + lines.join("\n");
                    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${fileName}_${moment().format("DDMMYYYY_HH_mm")}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    return null;
                })()
            }
        </>
    )
}

interface DialogProcessoExportarProps extends BtnExportarDadosProps {
    open: boolean,
    handleClose: () => void,
    exportType: TOpenModal
}